/* eslint-disable prettier/prettier */
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { RootState } from 'store';

import { autoAgencies, confirmAutoSolicitation, validateVehicle } from 'actions/autos';
import { fetchUserAccount } from 'actions/user';
import useFetching from 'hooks/useFetching';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { VehicleOption } from 'models/auto/vehicle';
import { TravelVehicleValidateResponse, VehicleValidateResponse } from 'models/auto/vehicleValidate';
import AutoDetails from 'screens/components/AutoDetails';
import { amplitudelogEvent, ViewTravelItemDetailEvent } from 'services/amplitudeService';
import { getAutoDetails, getAutoValidate } from 'services/autoService';
import { TravelArrangerUsersAllowed } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, extractErrorMessage } from 'utils/generic';

const INCOMPLETE_PROFILE_ERROR_MESSAGES = ["'Gender'", "'Phone'", "'DOB'"];

interface Props {
  show: boolean;
  auto?: VehicleOption;
  onClose: () => void;
  onSelect: (id?: string) => void;
  traveler?: TravelArrangerUsersAllowed;
  index?: number;
}

const AutoDetailsContainer = ({ auto, show, onClose, onSelect, traveler, index }: Props) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t } = useStoreTranslation();
  const history = useHistory();

  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<VehicleValidateResponse | undefined>();
  const [showChurning, setShowChurning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [optionId, setOptionId] = useState('');
  const [detailId, setDetailId] = useState('');
  const [validateId, setValidateId] = useState('');
  const [breakRulesMessage, setBreakRulesMessage] = useState<string[]>([]);
  const [unlimitedKm, setUnlimitedKm] = useState(false);

  const { searchId, sessionToken, agencies, userId, distanceRestriction, fullState } = useSelector((state: RootState) => ({
    searchId: state.autos.search?.searchId,
    sessionToken: state.user.accountResult?.sessionToken,
    agencies: state.autos.agencies,
    userId: state.user.accountResult?.id,
    distanceRestriction: state.autos.search?.distanceRestriction,
    fullState: state
  }));
  const userAccount = useUserAccount();
  const { pickUpPoint, dropOffPoint, pickUpDate, dropOffDate, search } = useSelector((state: RootState) => state.autos);

  useFetching(() => {
    if (!auto || !sessionToken || !searchId) {
      toast.add({ type: 'danger', message: t('defaultError.default') });
      history.replace('/autos');
      return;
    }
    const { optionId } = auto;

    if (optionId && searchId && sessionToken) {
      setOptionId(optionId);
      const getDetails = async () => {
        try {
          setIsLoading(true);
          const {
            data: { data, succeeded },
          } = await getAutoDetails({
            searchId,
            sessionToken,
            optionId: Number(optionId),
            userId: traveler ? traveler.id : userId,
            distanceRestriction: distanceRestriction ? distanceRestriction : 'International',
            travelArrangerUserId: traveler && traveler.id !== userId ? userId : undefined,
          });

          if (succeeded) {
            if (!!data.vehicleOption?.vehicle?.model && !!data.vehicleOption?.company?.name) {
              await dispatch(autoAgencies(data.vehicleOption.vehicle.model, data.vehicleOption.company.name));
            }
            if (typeof data.detailId !== 'undefined') {
              const isPreApproved =
                data.vehicleOption &&
                  data.vehicleOption.rates &&
                  data.vehicleOption.rates[0] &&
                  data.vehicleOption.rates[0].isPreApproved
                  ? data.vehicleOption.rates[0].isPreApproved
                  : false;
              const breakRulesMessage =
                data.vehicleOption &&
                  data.vehicleOption.rates &&
                  data.vehicleOption.rates[0] &&
                  data.vehicleOption.rates[0].breakRulesMessage
                  ? data.vehicleOption.rates[0].breakRulesMessage
                  : [];
              setDetailId(data.detailId);
              setBreakRulesMessage(breakRulesMessage);
              setUnlimitedKm(
                data.vehicleOption &&
                  data.vehicleOption.rates &&
                  data.vehicleOption.rates[0] &&
                  !!data.vehicleOption.rates[0].unlimitedMileage
                  ? true
                  : false,
              );
              const validate = await getAutoValidate(
                data.detailId,
                sessionToken,
                `${userId}`,
                `${distanceRestriction}`,
              );
              const details = { ...validate.data.data, breakRulesMessage, isPreApproved };
              setData(details);
              setValidateId(`${validate.data.data.validateId}`);

              // AMPLITUDE
              const amplitudeEvent: ViewTravelItemDetailEvent = {
                INDEX: index ?? 0,
                SOURCE: 'LIST',
                TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
                TRAVEL_FROM: pickUpPoint?.code ?? '',
                TRAVEL_TO: dropOffPoint?.code ?? '',
                TRAVEL_DESTINATION_TYPE:
                  search?.distanceRestriction && search?.distanceRestriction === 'Domestic'
                    ? 'NATIONAL'
                    : 'INTERNATIONAL',
                TRAVEL_TRIP_TYPE: '',
                TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
                TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
                TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
                TRAVEL_TRIP_LENGTH:
                  pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
                TRAVEL_PASSENGERS: '1',
                TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
                SEARCHID: search?.searchId ?? '',
                TRAVEL_PRODUCT_CODE: details?.vehicleOption?.company?.code ?? '',
                TRAVEL_PRODUCT_NAME: details?.vehicleOption?.company?.name ?? '',
                TRAVEL_SUB_PRODUCT: details?.vehicleOption?.vehicle?.model ?? '',
                TRAVEL_PRODUCT_CATEGORY: details?.vehicleOption?.vehicle?.vehicleCategory ?? '',
                TRAVEL_PRODUCT_PRICE:
                  details?.vehicleOption?.rates &&
                    details?.vehicleOption?.rates[0] &&
                    details?.vehicleOption?.rates[0].totalPricePayNow
                    ? `${details?.vehicleOption?.rates[0].totalPricePayNow}`
                    : '',
                TRAVEL_PRODUCT_PROVIDER: details?.vehicleOption?.provider ?? '',
                TRAVEL_FARE_TYPE: '',
              };
              amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_ITEM_DETAIL, amplitudeEvent, userAccount);
            }
          }
          setIsLoading(false);
        } catch (ex) {
          const status = ex?.response?.status;

          let message = t('autosDetails.error.genericValidate');
          if (status === 400) {
            message = t('autosDetails.error.unavailable');
          }

          Sentry.addBreadcrumb({
            category: 'auto',
            message: 'Auto validation solicitation error',
            data: { error: extractErrorMessage(ex), message },
          });

          setData(undefined);
          setIsLoading(false);

          toast.add({
            type: 'danger',
            message,
          });
          onClose();

          Sentry.captureMessage('[AutoDetails] Could not retrieve itinerary validation');
        }
      };
      if (show) getDetails();
    }
  }, [auto, show]);

  const handleConfirm = async (
    auto: VehicleValidateResponse,
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
  ) => {
    setIsCreating(true);
    try {
      await dispatch<any>(fetchUserAccount());

      const responseValidate = await validateVehicle(
        auto,
        justifyForBreakingRules,
        reason,
        detailId,
        validateId,
        optionId,
        breakRulesMessage,
        fullState,
        costCenterId,
      );

      if (!responseValidate) {
        throw false;
      }

      const vehicleValidate: TravelVehicleValidateResponse = {
        documentId: responseValidate.data.documentId,
        validationId: responseValidate.data.validationId,
        validateResultType: '',
      };

      await dispatch<any>(
        confirmAutoSolicitation(
          auto,
          justifyForBreakingRules,
          reason,
          detailId,
          validateId,
          optionId,
          breakRulesMessage,
          vehicleValidate,
          costCenterId,
          breakRulesMessageId
        ),
      );
      setShowSuccess(true);
    } catch (ex) {
      const status = ex?.response?.status;
      const error = extractErrorMessage(ex);

      setIsCreating(false);

      let message = t('autosDetails.error.generic');
      if (INCOMPLETE_PROFILE_ERROR_MESSAGES.some((msg) => error?.startsWith(msg))) {
        message = t('autosDetails.error.incompleteProfile');
      } else if (error?.includes('Churning')) {
        setShowChurning(true);
        message = '';
      } else if (status === 400) {
        message = t('autosDetails.error.unavailable');
      }

      if (error?.indexOf('DOB is not valid') !== -1) {
        message = t('autosDetails.error.dobNotValid');
      }

      Sentry.addBreadcrumb({
        category: 'auto',
        message: 'Confirm auto solicitation error',
        data: { error, message },
      });

      toast.add({
        type: 'danger',
        message: message || t('autosDetails.error.generic'),
      });

      Sentry.captureMessage('[AutoDetails] Could not create travel');
    }
  };

  return (
    <AutoDetails
      show={show}
      creating={isCreating}
      loading={isLoading}
      details={data}
      optionId={optionId}
      onClose={onClose}
      onConfirm={handleConfirm}
      onSelectAgency={onSelect}
      showChurning={showChurning}
      setShowChurning={setShowChurning}
      showSuccess={showSuccess}
      list={agencies?.vehicleOptions}
      unlimitedKm={unlimitedKm}
      traveler={traveler}
    />
  );
};

export default AutoDetailsContainer;
